import React from 'react';
import { MapPin, Upload, CheckCircle, Camera } from 'lucide-react';
import MapLocationPicker from './MapLocationPicker';

const BRAND_COLORS = {
  primary: '#1a1a1a',
  secondary: '#7C3AED',
  accent: '#C9A961',
  light: '#F5F5F0',
  lightGray: '#E8E8E3',
};

export const BasicInfoStep = ({ displayName, setDisplayName, role }) => (
  <div className="space-y-8">
    <div>
      <h3 className="text-2xl font-light tracking-wide mb-3" style={{ color: BRAND_COLORS.primary }}>
        Basic Information
      </h3>
      <p className="text-gray-600 font-light">Let's get to know you</p>
    </div>

    <div className="space-y-6">
      <div>
        <label className="block text-xs font-light tracking-widest mb-3 uppercase" style={{ color: BRAND_COLORS.primary }}>
          Full Name
        </label>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="w-full px-4 py-4 border-2 bg-transparent font-light transition-all duration-300 focus:outline-none focus:border-black"
          style={{ borderColor: BRAND_COLORS.lightGray }}
          required
        />
      </div>

      <div className="p-4 border-2 bg-gray-50" style={{ borderColor: BRAND_COLORS.lightGray }}>
        <p className="text-sm font-light text-gray-600">
          Signing up as: <span className="font-normal" style={{ color: BRAND_COLORS.primary }}>
            {role === 'professional' ? 'Professional' : 'Employer'}
          </span>
        </p>
      </div>
    </div>
  </div>
);

export const LocationStep = ({ location, setLocation, setError }) => {
  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-light tracking-wide mb-3" style={{ color: BRAND_COLORS.primary }}>
          Your Location
        </h3>
        <p className="text-gray-600 font-light">This helps us connect you with nearby opportunities</p>
      </div>

      <MapLocationPicker
        location={location}
        setLocation={setLocation}
        setError={setError}
      />
    </div>
  );
};

export const AddressStep = ({ address, setAddress, role }) => (
  <div className="space-y-8">
    <div>
      <h3 className="text-2xl font-light tracking-wide mb-3" style={{ color: BRAND_COLORS.primary }}>
        {role === 'employer' ? 'Business Address' : 'Your Address'}
      </h3>
      <p className="text-gray-600 font-light">
        {role === 'employer' ? 'Complete business address details' : 'Complete address details'}
      </p>
    </div>

    <div className="space-y-4">
      <input
        type="text"
        placeholder="Address Line"
        value={address.addressLine}
        onChange={(e) => setAddress({ ...address, addressLine: e.target.value })}
        className="w-full px-4 py-3 border-2 bg-transparent font-light transition-all duration-300 focus:outline-none focus:border-black"
        style={{ borderColor: BRAND_COLORS.lightGray }}
        required
      />
      <input
        type="text"
        placeholder="Landmark (optional)"
        value={address.landmark}
        onChange={(e) => setAddress({ ...address, landmark: e.target.value })}
        className="w-full px-4 py-3 border-2 bg-transparent font-light transition-all duration-300 focus:outline-none focus:border-black"
        style={{ borderColor: BRAND_COLORS.lightGray }}
      />
      <div className="grid grid-cols-2 gap-4">
        <input
          type="text"
          placeholder="City"
          value={address.city}
          onChange={(e) => setAddress({ ...address, city: e.target.value })}
          className="w-full px-4 py-3 border-2 bg-transparent font-light transition-all duration-300 focus:outline-none focus:border-black"
          style={{ borderColor: BRAND_COLORS.lightGray }}
          required
        />
        <input
          type="text"
          placeholder="State"
          value={address.state}
          onChange={(e) => setAddress({ ...address, state: e.target.value })}
          className="w-full px-4 py-3 border-2 bg-transparent font-light transition-all duration-300 focus:outline-none focus:border-black"
          style={{ borderColor: BRAND_COLORS.lightGray }}
        />
      </div>
      <input
        type="text"
        placeholder="Pincode"
        value={address.pincode}
        onChange={(e) => setAddress({ ...address, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
        className="w-full px-4 py-3 border-2 bg-transparent font-light transition-all duration-300 focus:outline-none focus:border-black"
        style={{ borderColor: BRAND_COLORS.lightGray }}
        required
      />
    </div>
  </div>
);

export const EssentialInfoStep = ({
  essentialInfo,
  setEssentialInfo,
  setError
}) => {
  const calculateAge = (dob) => {
    if (!dob || dob.length !== 10) return '';

    const [day, month, year] = dob.split('/').map(num => parseInt(num));
    if (!day || !month || !year || year < 1900 || year > new Date().getFullYear()) return '';

    const birthDate = new Date(year, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  const handleDOBChange = (value) => {
    // Format as DD/MM/YYYY and allow only numbers and slashes
    let formatted = value.replace(/[^\d/]/g, '');

    // Auto-add slashes
    if (formatted.length === 2 && !formatted.includes('/')) {
      formatted += '/';
    } else if (formatted.length === 5 && formatted.split('/').length === 2) {
      formatted += '/';
    }

    // Limit to 10 characters
    formatted = formatted.slice(0, 10);

    setEssentialInfo({ ...essentialInfo, dateOfBirth: formatted });

    // Calculate age if DOB is complete
    if (formatted.length === 10) {
      const age = calculateAge(formatted);
      if (age && age >= 18 && age <= 100) {
        setEssentialInfo({ ...essentialInfo, dateOfBirth: formatted, age });
      } else if (age) {
        setError('Age must be between 18 and 100 years');
      }
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-light tracking-wide mb-3" style={{ color: BRAND_COLORS.primary }}>
          Essential Information
        </h3>
        <p className="text-gray-600 font-light leading-relaxed">
          Please bear with us as we take you through following essential questions.
          Your responses will help us understand your profile better and recommend
          the most suitable job opportunities. Once completed, you're all set to go!
        </p>
      </div>

      <div className="space-y-6">
        {/* Date of Birth */}
        <div>
          <label className="block text-xs font-light tracking-widest mb-3 uppercase" style={{ color: BRAND_COLORS.primary }}>
            Date of Birth (DD/MM/YYYY) *
          </label>
          <input
            type="text"
            value={essentialInfo.dateOfBirth}
            onChange={(e) => handleDOBChange(e.target.value)}
            placeholder="DD/MM/YYYY"
            className="w-full px-4 py-3 border-2 bg-transparent font-light transition-all duration-300 focus:outline-none focus:border-black"
            style={{ borderColor: BRAND_COLORS.lightGray }}
            required
          />
          {essentialInfo.age && (
            <p className="text-xs font-light text-gray-500 mt-2">
              Age: {essentialInfo.age} years
            </p>
          )}
        </div>

        {/* Sex */}
        <div>
          <label className="block text-xs font-light tracking-widest mb-3 uppercase" style={{ color: BRAND_COLORS.primary }}>
            Sex *
          </label>
          <div className="grid grid-cols-2 gap-4">
            {['Male', 'Female'].map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setEssentialInfo({ ...essentialInfo, sex: option })}
                className={`py-3 px-4 border-2 font-light tracking-wide transition-all duration-300 ${essentialInfo.sex === option
                  ? 'border-black bg-black text-white'
                  : 'bg-transparent hover:border-black'
                  }`}
                style={{ borderColor: essentialInfo.sex === option ? BRAND_COLORS.primary : BRAND_COLORS.lightGray }}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        {/* Aadhaar Linked Mobile */}
        <div>
          <label className="block text-xs font-light tracking-widest mb-3 uppercase" style={{ color: BRAND_COLORS.primary }}>
            Aadhaar Linked Mobile Number *
          </label>
          <p className="text-xs font-light text-gray-600 mb-2">
            Provide the number which is linked to Aadhaar for verification.
            You have to upload a copy of Aadhaar at a later step.
          </p>
          <input
            type="tel"
            value={essentialInfo.aadhaarLinkedMobile}
            onChange={(e) => setEssentialInfo({
              ...essentialInfo,
              aadhaarLinkedMobile: e.target.value.replace(/\D/g, '').slice(0, 10)
            })}
            placeholder="10-digit mobile number"
            className="w-full px-4 py-3 border-2 bg-transparent font-light transition-all duration-300 focus:outline-none focus:border-black"
            style={{ borderColor: BRAND_COLORS.lightGray }}
            required
          />
        </div>

        {/* Employment Status */}
        <div>
          <label className="block text-xs font-light tracking-widest mb-3 uppercase" style={{ color: BRAND_COLORS.primary }}>
            How Would You Describe Yourself? *
          </label>
          <div className="space-y-2">
            {[
              'Student',
              'Not employed',
              'Employed but available on weekends',
              'Currently looking for a job and available until placed'
            ].map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setEssentialInfo({ ...essentialInfo, employmentStatus: option })}
                className={`w-full py-3 px-4 border-2 font-light text-left transition-all duration-300 ${essentialInfo.employmentStatus === option
                  ? 'border-black bg-black text-white'
                  : 'bg-transparent hover:border-black'
                  }`}
                style={{ borderColor: essentialInfo.employmentStatus === option ? BRAND_COLORS.primary : BRAND_COLORS.lightGray }}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        {/* Current Course (if student) */}
        {essentialInfo.employmentStatus === 'Student' && (
          <div>
            <label className="block text-xs font-light tracking-widest mb-3 uppercase" style={{ color: BRAND_COLORS.primary }}>
              Which Course Have You Enrolled For? *
            </label>
            <input
              type="text"
              value={essentialInfo.currentCourse}
              onChange={(e) => setEssentialInfo({ ...essentialInfo, currentCourse: e.target.value })}
              placeholder="e.g., B.Tech Computer Science"
              className="w-full px-4 py-3 border-2 bg-transparent font-light transition-all duration-300 focus:outline-none focus:border-black"
              style={{ borderColor: BRAND_COLORS.lightGray }}
              required
            />
          </div>
        )}

        {/* Highest Education */}
        <div>
          <label className="block text-xs font-light tracking-widest mb-3 uppercase" style={{ color: BRAND_COLORS.primary }}>
            Highest Educational Qualification *
          </label>
          <div className="grid grid-cols-2 gap-4">
            {['10th standard', '12th standard', 'Degree', 'Masters'].map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setEssentialInfo({ ...essentialInfo, highestEducation: option })}
                className={`py-3 px-4 border-2 font-light tracking-wide transition-all duration-300 ${essentialInfo.highestEducation === option
                  ? 'border-black bg-black text-white'
                  : 'bg-transparent hover:border-black'
                  }`}
                style={{ borderColor: essentialInfo.highestEducation === option ? BRAND_COLORS.primary : BRAND_COLORS.lightGray }}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        {/* Work Experience */}
        <div>
          <label className="block text-xs font-light tracking-widest mb-3 uppercase" style={{ color: BRAND_COLORS.primary }}>
            Have You Worked Before? *
          </label>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Yes', value: true },
              { label: 'No', value: false }
            ].map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() => setEssentialInfo({ ...essentialInfo, hasWorkedBefore: option.value })}
                className={`py-3 px-4 border-2 font-light tracking-wide transition-all duration-300 ${essentialInfo.hasWorkedBefore === option.value
                  ? 'border-black bg-black text-white'
                  : 'bg-transparent hover:border-black'
                  }`}
                style={{ borderColor: essentialInfo.hasWorkedBefore === option.value ? BRAND_COLORS.primary : BRAND_COLORS.lightGray }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const IDUploadStep = ({ idFile, setIdFile, setError }) => (
  <div className="space-y-8">
    <div>
      <h3 className="text-2xl font-light tracking-wide mb-3" style={{ color: BRAND_COLORS.primary }}>
        Identity Verification
      </h3>
      <p className="text-gray-600 font-light">Upload a government-issued ID</p>
    </div>

    <div className="border-2 border-dashed p-8 text-center" style={{ borderColor: BRAND_COLORS.lightGray }}>
      {idFile ? (
        <div className="space-y-4">
          <CheckCircle className="w-12 h-12 mx-auto" style={{ color: BRAND_COLORS.accent }} />
          <p className="font-light" style={{ color: BRAND_COLORS.primary }}>{idFile.name}</p>
          <button
            type="button"
            onClick={() => setIdFile(null)}
            className="text-xs font-light tracking-wider text-gray-500 hover:opacity-70"
          >
            CHANGE FILE
          </button>
        </div>
      ) : (
        <label className="cursor-pointer block">
          <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="font-light mb-2" style={{ color: BRAND_COLORS.primary }}>
            Click to upload
          </p>
          <p className="text-xs font-light text-gray-500">
            JPG, PNG, PDF or DOC (max 5MB)
          </p>
          <input
            type="file"
            accept="image/*,.pdf,.doc,.docx"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file && file.size <= 5 * 1024 * 1024) {
                setIdFile(file);
              } else {
                setError('File size must be less than 5MB');
              }
            }}
            className="hidden"
          />
        </label>
      )}
    </div>
  </div>
);

export const ProfilePictureStep = ({ profilePicture, setProfilePicture, setError }) => (
  <div className="space-y-8">
    <div>
      <h3 className="text-2xl font-light tracking-wide mb-3" style={{ color: BRAND_COLORS.primary }}>
        Profile Picture
      </h3>
      <p className="text-gray-600 font-light">Add a photo to help others recognize you</p>
    </div>

    <div className="flex flex-col items-center justify-center py-8">
      <label className="cursor-pointer relative">
        <div
          className="w-40 h-40 rounded-full items-center justify-center border-2 border-dashed overflow-hidden bg-gray-50"
          style={{ borderColor: BRAND_COLORS.lightGray }}
        >
          {profilePicture ? (
            <img
              src={URL.createObjectURL(profilePicture)}
              alt="Profile Preview"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Camera className="w-12 h-12 text-gray-400" />
            </div>
          )}
        </div>

        <div className="absolute bottom-0 right-0 bg-black p-3 rounded-full border-4 border-white">
          <Upload className="w-5 h-5 text-white" />
        </div>

        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file && file.size <= 5 * 1024 * 1024) {
              setProfilePicture(file);
              setError('');
            } else {
              setError('File size must be less than 5MB');
            }
          }}
          className="hidden"
        />
      </label>

      <p className="mt-6 text-sm text-gray-500 font-normal text-center">
        {profilePicture ? 'Click to change photo' : 'Click to upload photo'}
      </p>
    </div>
  </div>
);

export const CompletionStep = () => (
  <div className="space-y-8 text-center">
    <div className="w-24 h-24 mx-auto border-2 flex items-center justify-center" style={{ borderColor: BRAND_COLORS.accent }}>
      <CheckCircle className="w-12 h-12" style={{ color: BRAND_COLORS.accent }} />
    </div>
    <div>
      <h3 className="text-2xl font-light tracking-wide mb-3" style={{ color: BRAND_COLORS.primary }}>
        Ready to Begin
      </h3>
      <p className="text-gray-600 font-light leading-relaxed max-w-md mx-auto">
        Your profile is complete. Click below to finish setup and start your journey with SkillVend.
      </p>
    </div>
  </div>
);
